import React, { useEffect, useState } from 'react';
import { notification } from 'antd';
import { Drawer } from '@prio365/prio365-react-library';
import { useTranslation } from 'react-i18next';
import { TaskForm } from './TaskForm';
import { apiCreateJiraTask, apiCreateTask, apiCreateTodoTask } from '../api';
import { CreateJiraTask, CreateTaskRequest } from '../../../models/Task';
import TodoForm from './Todo/TodoForm';
import { CreateTodoTaskRequest, TodoTaskFormModel } from '../../../models/Todo';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Project } from '../../../models/Project';
import {
  getAllProjects,
  getProject,
  getUserMe,
  getWidgetAreaDisplayState,
  RootReducerState,
} from '../../../apps/main/rootReducer';
import { MessageId } from '../../../models/Types';
import JiraTask from './JiraTask';
import { setNewTaskCreated } from '../../widgetArea/actions';
import { fetchProjects } from '../../projects/actions';
import { SettingKey } from '../../../models/Settings';
import { apiFetchSettings } from '../../settings/api';

interface TasksDrawerProps {
  visible: boolean;
  preSelectedTask?: CreateTaskRequest;
  preSelectedTodoTask?: CreateTodoTaskRequest;
  sourceEmailUrl?: string;
  messageId?: MessageId;
  closeDrawer: VoidFunction;
  isMe?: boolean;
}

export const TasksDrawer: React.FC<TasksDrawerProps> = (props) => {
  const {
    visible,
    preSelectedTask,
    closeDrawer,
    sourceEmailUrl,
    messageId,
    isMe,
  } = props;

  const userMe = useSelector(getUserMe);
  var currentProject: Project;
  const dispatch = useDispatch();
  const { open } = useSelector(getWidgetAreaDisplayState);

  currentProject = useSelector<RootReducerState, Project>((state) =>
    getProject(state, preSelectedTask && preSelectedTask.projectId)
  );
  const hasPlanner = currentProject?.projectExtensionMeDtos.some(
    (extension) => extension?.projectExtensionType === 'planner'
  );
  const defaultTab = hasPlanner ? 'planner' : 'todo';

  const projects = useSelector(getAllProjects).filter(
    (project) => project.isArchived === false
  );

  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [haveJiraConfig, setHaveJiraConfig] = useState<boolean>(false);

  const jiraTabVisible = isMe
    ? projects
        ?.flatMap((project) => project.projectExtensionMeDtos)
        .some((extension) => extension?.projectExtensionType === 'jira') &&
      haveJiraConfig
    : currentProject?.projectExtensionMeDtos.some(
        (extension) => extension?.projectExtensionType === 'jira'
      ) && haveJiraConfig;

  const handleFinish = async (
    value: CreateTaskRequest,
    shouldOpen?: boolean
  ) => {
    value.sourceEmailUrl = sourceEmailUrl;
    value.messageId = messageId;
    setIsSaving(true);
    const { result, data } = await apiCreateTask(value.projectId, value);
    setIsSaving(false);
    if (result.status >= 200 && result.status < 300) {
      if (open) {
        dispatch(setNewTaskCreated(true));
      }

      // Open task in new tab if requested
      if (shouldOpen && data?.id) {
        const plannerUrl = currentProject?.masterPlanId
          ? `https://planner.cloud.microsoft/webui/plan/${currentProject.masterPlanId}/view/board/task/${data.id}`
          : '';
        if (plannerUrl) {
          window.open(plannerUrl, '_blank');
        }
      }

      closeDrawer();
      return true;
    } else {
      notification.open({
        message: t('common:error'),
        description: t('tasks:form.errorMessages.createTask'),
      });
      return false;
    }
  };

  const handleFinishJiraTask = async (value: CreateJiraTask) => {
    value.messageLink = sourceEmailUrl;
    value.prioProjectId = isMe
      ? projects.find((project) =>
          project.projectExtensionMeDtos.some(
            (extension) =>
              extension?.projectExtensionType === 'jira' &&
              extension?.key === value.jiraProjectId
          )
        )?.projectId
      : currentProject.projectId;
    value.messageId = messageId;
    setIsSaving(true);
    const { result } = await apiCreateJiraTask(value);
    setIsSaving(false);
    if (result.status >= 200 && result.status < 300) {
      if (open) {
        dispatch(setNewTaskCreated(true));
      }
      closeDrawer();
      return true;
    } else {
      notification.open({
        message: t('common:error'),
        description: t('tasks:form.errorMessages.createTask'),
      });
      return false;
    }
  };

  const handleFinishTodoTask = async (value: TodoTaskFormModel) => {
    const { dueDate: dueDateTimeMoment, reminderDate: reminderDateTimeMoment } =
      value;
    const todoTask: CreateTodoTaskRequest = {
      todoTask: {
        title: value.title,
        status: value.status,
        importance: value.importance,
        isReminderOn: value.reminderDate ? true : false,
        dueDateTime: dueDateTimeMoment && {
          dateTime: dueDateTimeMoment?.toISOString(),
          timeZone: moment.tz.guess(),
        },
        reminderDateTime: reminderDateTimeMoment && {
          dateTime: reminderDateTimeMoment?.toISOString(),
          timeZone: moment.tz.guess(),
        },
        body: {
          content: value.description,
        },
      },
    };
    setIsSaving(true);
    const { result } = await apiCreateTodoTask(value.todoListId, todoTask);
    setIsSaving(true);
    if (result.status >= 200 && result.status < 300) {
      closeDrawer();
      return true;
    } else {
      notification.open({
        message: t('common:error'),
        description: t('tasks:todoForm.errorMessages.createTask'),
      });
      closeDrawer();
      return false;
    }
  };
  const handleCancel = () => {
    closeDrawer();
  };
  useEffect(() => {
    if (visible) {
      dispatch(fetchProjects(true));
    }
  }, [dispatch, visible]);

  useEffect(() => {
    if (!visible) return;

    const fetchProjectExtensionUserConfiguration = async () => {
      var setting: SettingKey = {
        keys: ['ProjectExtension_Jira_UserConnectionOptions'],
        scopeId: userMe.id,
        eSettingScope: 'user',
      };
      const { data, result } = await apiFetchSettings([setting]);

      if (result.status >= 200 && result.status < 300) {
        setHaveJiraConfig(data?.[0]?.couldNotBeLoaded === false);
      }
    };

    fetchProjectExtensionUserConfiguration();
  }, [visible, userMe]);

  return (
    <Drawer
      title={t('tasks:modal.title')}
      footer={null}
      visible={visible}
      onClose={handleCancel}
      defaultTab={defaultTab}
      tabs={[
        ...(currentProject
          ? [
              ...(hasPlanner
                ? [
                    {
                      label: 'Planner',
                      key: 'planner',
                      content: (
                        <TaskForm
                          actionLabel={t('tasks:form.actionLabel')}
                          cancelLabel={t('common:actions.cancel')}
                          onFinish={handleFinish}
                          onCancel={handleCancel}
                          disableForm={isSaving}
                          resetOnFinish
                          //@ts-ignore
                          initialValues={preSelectedTask}
                          isDrawer={visible}
                          disableActionButton={isSaving}
                        />
                      ),
                    },
                  ]
                : []),
            ]
          : []),
        {
          label: 'ToDo',
          key: 'todo',
          content: (
            <TodoForm
              actionLabel={t('common:save')}
              cancelLabel={t('common:actions.cancel')}
              onFinish={handleFinishTodoTask}
              onCancel={handleCancel}
              initialValues={{
                todoListId: null,
                title: preSelectedTask && preSelectedTask.title,
                description: preSelectedTask && preSelectedTask.description,
                dueDate: preSelectedTask && moment(preSelectedTask.dueDateTime),
              }}
              isDrawer={visible}
              resetOnFinish
            />
          ),
        },
        ...(jiraTabVisible
          ? [
              {
                label: 'Jira',
                key: 'jira',
                content: (
                  <JiraTask
                    actionLabel={t('common:save')}
                    cancelLabel={t('common:actions.cancel')}
                    onFinish={handleFinishJiraTask}
                    onCancel={handleCancel}
                    initialValues={{
                      summary: preSelectedTask && preSelectedTask.title,
                      description:
                        preSelectedTask && preSelectedTask.description,
                      jiraProjectId: isMe
                        ? projects
                            ?.flatMap(
                              (project) => project.projectExtensionMeDtos
                            )
                            .find(
                              (extension) =>
                                extension?.projectExtensionType === 'jira'
                            )?.value
                        : currentProject?.projectExtensionMeDtos.find(
                            (extension) =>
                              extension?.projectExtensionType === 'jira'
                          )?.value,
                    }}
                    isDrawer={visible}
                    disableActionButton={isSaving}
                    resetOnFinish
                    projectExtensionsMe={
                      isMe
                        ? projects
                            ?.flatMap(
                              (project) => project.projectExtensionMeDtos
                            )
                            .filter(
                              (extension) =>
                                extension?.projectExtensionType === 'jira'
                            )
                        : currentProject?.projectExtensionMeDtos.filter(
                            (extension) =>
                              extension?.projectExtensionType === 'jira'
                          )
                    }
                  />
                ),
              },
            ]
          : []),
      ]}
    ></Drawer>
  );
};

export default TasksDrawer;
